import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Context from "../../Context";
import { Header } from "../../components/modules";
import { useToast } from "../../components/ui";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components";

// Icons
import { IoReceiptOutline } from "react-icons/io5";
import {
  FaMagnifyingGlass,
  FaXmark,
  FaArrowRotateRight,
  FaGift,
  FaEye,
  FaTicket,
  FaLayerGroup,
  FaCopy,
  FaUsers,
  FaReceipt,
} from "react-icons/fa6";

const PAGE_SIZE = 50;

export const MissionOrders = () => {
  const navigate = useNavigate();
  const { callApi, configs } = Context();
  const { toast } = useToast();

  // Data States
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [roundDate, setRoundDate] = useState(configs?.roundDate || "");

  // Search States
  const [searchValue, setSearchValue] = useState("");
  const [searchType, setSearchType] = useState("shortId");

  const lastIdRef = useRef(null);
  const sentinelRef = useRef(null);

  const getMappedSearchType = (type) => {
    if (["phone", "firstName", "lastName", "lineTitle"].includes(type)) {
      return `userData.${type}`;
    }
    return type;
  };

  const searchQueries = searchValue.trim()
    ? [[getMappedSearchType(searchType), searchValue.trim()]]
    : [];

  useEffect(() => {
    if (configs?.roundDate && !roundDate) {
      setRoundDate(configs.roundDate);
    }
  }, [configs, roundDate]);

  useEffect(() => {
    getOrders(searchQueries);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundDate]);

  // Observer for Infinite Scroll
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !loadingMore && !loading) {
          fetchMore();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loadingMore, loading, orders]);

  const buildQuery = (extraQueries = [], startAfter = "") => ({
    roundDate: roundDate || null,
    queries: extraQueries,
    limit: PAGE_SIZE,
    orderBy: ["createdAt", "desc"],
    ...(startAfter ? { startAfter } : {}),
  });

  // Fresh search / load
  const getOrders = async (extraQueries = searchQueries) => {
    setLoading(true);
    lastIdRef.current = null;
    try {
      const result = await callApi(
        "missions-getAllMissionOrders",
        buildQuery(extraQueries),
        { notShowLoading: true }
      );
      const data = result || [];
      setOrders(data);
      setHasMore(data.length === PAGE_SIZE);
      lastIdRef.current = data.at(-1)?.id ?? null;
    } catch (err) {
      toast({
        className: "bg-rose-600 text-white",
        title: "เกิดข้อผิดพลาดในการโหลดรายการประวัติภารกิจ",
      });
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Next Page
  const fetchMore = async () => {
    if (!lastIdRef.current) return;
    setLoadingMore(true);
    try {
      const result = await callApi(
        "missions-getAllMissionOrders",
        buildQuery(searchQueries, lastIdRef.current),
        { notShowLoading: true }
      );
      const data = result || [];
      setOrders((prev) => [...prev, ...data]);
      setHasMore(data.length === PAGE_SIZE);
      lastIdRef.current = data.at(-1)?.id ?? null;
    } catch (err) {
      // ignore
    } finally {
      setLoadingMore(false);
    }
  };

  // Quick stats computed from current view
  const stats = useMemo(() => {
    const total = orders.length;
    const uniqueUsers = new Set(orders.map((o) => o.userId).filter(Boolean)).size;
    const totalLottoOrdersUsed = orders.reduce((sum, o) => {
      const refs = o.orderReference || o.orders || [];
      return sum + (Array.isArray(refs) ? refs.length : 0);
    }, 0);
    const rewardsIssued = orders.filter((o) => o.rewardId || o.reward?.id).length;
    return { total, uniqueUsers, totalLottoOrdersUsed, rewardsIssued };
  }, [orders]);

  const copyToClipboard = (text, label = "คัดลอกสำเร็จ") => {
    navigator.clipboard.writeText(text);
    toast({
      className: "bg-blue text-white",
      title: label,
    });
  };

  return (
    <div className="h-fit w-full max-w-[1600px] mx-auto pb-12" id="main">
      {/* Header */}
      <Header
        header="Missions"
        subHeader="ประวัติการทำภารกิจ"
        icon={<IoReceiptOutline className="text-blue" />}
      />

      {/* ── Dashboard Stats Bar ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        {/* Total Transactions */}
        <div className="bg-white rounded-md p-4 shadow-sm border border-gray-200 flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500">ประวัติการทำภารกิจในงวด</p>
            <p className="text-2xl font-bold text-navy mt-1">{stats.total}</p>
          </div>
          <div className="w-10 h-10 rounded-md bg-blue/10 text-blue flex items-center justify-center text-lg">
            <FaLayerGroup />
          </div>
        </div>

        {/* Unique Customers */}
        <div className="bg-white rounded-md p-4 shadow-sm border border-gray-200 flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500">ลูกค้าที่ร่วมภารกิจ</p>
            <p className="text-2xl font-bold text-navy mt-1">{stats.uniqueUsers}</p>
          </div>
          <div className="w-10 h-10 rounded-md bg-purple-50 text-purple-600 flex items-center justify-center text-lg">
            <FaUsers />
          </div>
        </div>

        {/* Total Lotto Orders Claimed */}
        <div className="bg-white rounded-md p-4 shadow-sm border border-gray-200 flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-amber-600">ออเดอร์สลากที่ใช้ทำภารกิจ</p>
            <p className="text-2xl font-bold text-amber-600 mt-1">{stats.totalLottoOrdersUsed} ออเดอร์</p>
          </div>
          <div className="w-10 h-10 rounded-md bg-amber-50 text-amber-600 flex items-center justify-center text-lg">
            <FaReceipt />
          </div>
        </div>

        {/* Rewards Issued */}
        <div className="bg-white rounded-md p-4 shadow-sm border border-gray-200 flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-emerald-600">คูปองรางวัลที่แจกแล้ว</p>
            <p className="text-2xl font-bold text-emerald-600 mt-1">{stats.rewardsIssued} ใบ</p>
          </div>
          <div className="w-10 h-10 rounded-md bg-emerald-50 text-emerald-600 flex items-center justify-center text-lg">
            <FaGift />
          </div>
        </div>
      </div>

      {/* ── Toolbar: Round Selector & Search ─────────────────────────────────── */}
      <div className="bg-white w-full rounded-md shadow-sm mt-4 border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex flex-col md:flex-row md:items-center justify-between gap-4">
          {/* Left: Round Selector */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-gray-700 whitespace-nowrap">งวด:</span>
            <select
              className="border border-gray-300 rounded-md py-1.5 px-3 text-xs font-bold text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue bg-white"
              onChange={(e) => setRoundDate(e.target.value)}
              value={roundDate || ""}
            >
              {(configs?.roundDates || []).map((date) => (
                <option key={date} value={date}>
                  {date}
                </option>
              ))}
            </select>
          </div>

          {/* Right: Search Box */}
          <div className="flex items-center gap-2">
            <select
              className="border border-gray-300 rounded-md py-2 px-2.5 text-xs font-bold text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue bg-white"
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
            >
              <option value="shortId">รหัสทำรายการ</option>
              <option value="phone">เบอร์โทรศัพท์</option>
              <option value="userId">รหัสผู้ใช้</option>
              <option value="firstName">ชื่อ</option>
              <option value="lastName">นามสกุล</option>
              <option value="lineTitle">ชื่อไลน์</option>
            </select>

            <div className="relative">
              <FaMagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
              <input
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && getOrders(searchQueries)}
                placeholder="พิมพ์ค้นหาแล้วกด Enter..."
                className="pl-8 pr-7 py-2 border border-gray-300 rounded-md text-xs font-semibold placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue w-60 box-border"
              />
              {searchValue && (
                <button
                  onClick={() => {
                    setSearchValue("");
                    getOrders([]);
                  }}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
                >
                  <FaXmark />
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={() => getOrders(searchQueries)}
              className="bg-blue hover:opacity-90 py-2 px-3 text-xs font-bold rounded-md text-white shadow-sm flex items-center gap-1.5 transition-all"
            >
              <FaMagnifyingGlass /> ค้นหา
            </button>

            <button
              type="button"
              onClick={() => {
                setSearchValue("");
                getOrders([]);
              }}
              title="รีเฟรชข้อมูล"
              className="p-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <FaArrowRotateRight className={`text-sm ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {/* ── Table ───────────────────────────────────────────────────────────── */}
        <Table>
          <TableHeader className="bg-gray-50 border-b border-gray-200">
            <TableRow>
              <TableHead className="w-12 text-center text-xs font-bold text-gray-500">#</TableHead>
              <TableHead className="w-48 text-center text-xs font-bold text-gray-500">วัน - เวลา</TableHead>
              <TableHead className="w-28 text-center text-xs font-bold text-gray-500">รหัสทำรายการ</TableHead>
              <TableHead className="text-left text-xs font-bold text-gray-500">ภารกิจที่สำเร็จ</TableHead>
              <TableHead className="text-left text-xs font-bold text-gray-500">ลูกค้า / ผู้ใช้งาน</TableHead>
              <TableHead className="text-left text-xs font-bold text-gray-500">ออเดอร์สลากที่ใช้แลก</TableHead>
              <TableHead className="text-left text-xs font-bold text-gray-500">คูปองรางวัลที่ได้รับ</TableHead>
              <TableHead className="w-24 text-center text-xs font-bold text-gray-500">ดูข้อมูล</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="h-64 text-center">
                  <div className="flex flex-col items-center justify-center gap-2 text-gray-400">
                    <div className="w-8 h-8 border-4 border-navy border-t-transparent rounded-full animate-spin" />
                    <p className="text-sm font-semibold">กำลังโหลดประวัติภารกิจ...</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="h-64 text-center">
                  <div className="flex flex-col items-center justify-center gap-2 text-gray-400">
                    <IoReceiptOutline className="text-4xl text-gray-300" />
                    <p className="text-sm font-semibold">ไม่พบข้อมูลการทำภารกิจในงวดนี้</p>
                    {searchValue && (
                      <p className="text-xs text-gray-400">
                        ลองเปลี่ยนคำค้นหา หรือ{" "}
                        <button
                          onClick={() => {
                            setSearchValue("");
                            getOrders([]);
                          }}
                          className="text-blue underline font-semibold"
                        >
                          ล้างคำค้นหา
                        </button>
                      </p>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              <>
                {orders.map((order, index) => (
                  <OrderRow
                    key={order.id || index}
                    order={order}
                    index={index}
                    onView={() => navigate(`/mission/${order.id}`)}
                    onCopyId={copyToClipboard}
                  />
                ))}

                {/* Sentinel for infinite scroll */}
                <TableRow ref={sentinelRef}>
                  <TableCell colSpan={8} className="text-center py-3">
                    {loadingMore && (
                      <div className="flex items-center justify-center gap-2 text-xs font-semibold text-gray-400">
                        <div className="w-4 h-4 border-2 border-navy border-t-transparent rounded-full animate-spin" />
                        กำลังโหลดข้อมูลเพิ่มเติม...
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

// ── Single Row Component ───────────────────────────────────────────────────────

const OrderRow = ({ order, index, onView, onCopyId }) => {
  const fullName = order?.userData
    ? `${order.userData.firstName || ""} ${order.userData.lastName || ""}`.trim() || "-"
    : "-";

  const lottoOrderRefs = Array.isArray(order.orderReference)
    ? order.orderReference
    : Array.isArray(order.orders)
      ? order.orders
      : [];

  return (
    <TableRow
      className="hover:bg-gray-50/80 transition-colors cursor-pointer group"
      onClick={onView}
    >
      {/* Index */}
      <TableCell className="text-center font-semibold text-gray-500 text-xs">
        {index + 1}
      </TableCell>

      {/* Datetime */}
      <TableCell className="text-center text-xs font-medium text-gray-600 whitespace-nowrap">
        {order.createdAt
          ? `${new Date(order.createdAt).toLocaleDateString("th-TH", {
            year: "numeric",
            month: "short",
            day: "numeric",
          })} ${new Date(order.createdAt).toLocaleTimeString("th-TH", {
            hour: "2-digit",
            minute: "2-digit",
          })} น.`
          : "-"}
      </TableCell>

      {/* Short ID */}
      <TableCell className="text-center">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onCopyId(order.shortId || order.id, "คัดลอกรหัสทำรายการสำเร็จ");
          }}
          title="คลิกเพื่อคัดลอก"
          className="inline-flex items-center gap-1 font-mono text-xs font-bold text-navy bg-gray-100 hover:bg-gray-200 px-2 py-0.5 rounded-md transition-colors"
        >
          {order.shortId || order.id?.slice(-5).toUpperCase()}
          <FaCopy className="text-[10px] text-gray-400 group-hover:text-gray-600" />
        </button>
      </TableCell>

      {/* Mission */}
      <TableCell>
        <div className="flex items-center gap-2.5 max-w-xs">
          <div className="w-9 h-9 rounded-md bg-gray-100 border border-gray-200 shrink-0 overflow-hidden flex items-center justify-center">
            {order.mission?.image ? (
              <img
                src={order.mission.image}
                alt={order.mission.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <FaGift className="text-sm text-blue" />
            )}
          </div>
          <div className="min-w-0">
            <p className="text-xs font-bold text-gray-800 truncate">
              {order.mission?.name || "ภารกิจ"}
            </p>
            <p className="text-[11px] text-gray-400 line-clamp-1">
              {order.mission?.type || ""}
            </p>
          </div>
        </div>
      </TableCell>

      {/* Customer Info */}
      <TableCell>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-bold text-gray-800 truncate">{fullName}</span>
          </div>
          <div className="flex items-center gap-2 mt-0.5 text-[11px] text-gray-500">
            <span>{order.userData?.phone || "-"}</span>
            <span
              onClick={(e) => {
                e.stopPropagation();
                window.open(
                  `${window.location.origin}/user/${order?.userId}`,
                  "_blank"
                );
              }}
              title="เปิดหน้าผู้ใช้งาน"
              className="text-blue hover:underline cursor-pointer font-mono"
            >
              #{order.userId?.slice(-6) || "-"}
            </span>
          </div>
        </div>
      </TableCell>

      {/* Lotto Orders Used */}
      <TableCell>
        {lottoOrderRefs.length > 0 ? (
          <div className="flex flex-wrap items-center gap-1 max-w-xs">
            {lottoOrderRefs.map((orderId, idx) => {
              const displayId = typeof orderId === "string" ? orderId : orderId?.id || orderId?.orderId || "-";
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(`/orders/${displayId}`, "_blank");
                  }}
                  title={`คลิกเพื่อดูคำสั่งซื้อสลาก ${displayId}`}
                  className="inline-flex items-center gap-0.5 text-[11px] font-mono font-bold bg-amber-50 text-amber-800 hover:bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded-md transition-colors"
                >
                  <FaReceipt className="text-[9px] text-amber-600" />
                  <span>#{displayId.slice(-6).toUpperCase()}</span>
                </button>
              );
            })}
          </div>
        ) : (
          <span className="text-[11px] text-gray-400 font-medium">
            {order.mission?.type === "custom" ? "ภารกิจกำหนดเอง" : "-"}
          </span>
        )}
      </TableCell>

      {/* Reward */}
      <TableCell>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-800 truncate">
            <FaTicket className="text-emerald-600 text-xs shrink-0" />
            <span className="truncate">{order.mission?.reward?.name || "คูปองรางวัล"}</span>
          </div>
          {order.rewardId && (
            <div
              onClick={(e) => {
                e.stopPropagation();
                window.open(
                  `${window.location.origin}/coupon/${order.rewardId}`,
                  "_blank"
                );
              }}
              className="text-[11px] font-mono text-blue hover:underline cursor-pointer mt-0.5"
            >
              คูปอง: #{order.rewardId.slice(-6).toUpperCase()}
            </div>
          )}
        </div>
      </TableCell>

      {/* Action */}
      <TableCell className="text-center">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onView();
          }}
          className="py-1.5 px-3 rounded-md bg-blue hover:opacity-90 text-white text-xs font-bold flex items-center justify-center gap-1 mx-auto transition-all"
        >
          <FaEye className="text-[10px]" /> ดูข้อมูล
        </button>
      </TableCell>
    </TableRow>
  );
};

export default MissionOrders;

